import type { Properties } from 'js-java-properties'
import { getProperty, listProperties, setProperty, toMap } from 'js-java-properties'

import { SERIAL_PORT_CONFIG_DEFAULTS } from '@/features/controller/configuration/models/raspiProperties/defaults'
import type { ConfigWithErrors } from '@/features/controller/configuration/models/raspiProperties/errors'
import { extractErrors } from '@/features/controller/configuration/models/raspiProperties/errors'
import {
  readBareOrFullSerialPortName,
  readSerialMode,
  writeBareSerialPortName,
  writeSerialMode,
} from '@/features/controller/configuration/models/raspiProperties/serialPort'
import type {
  ExternalModbusClientReference,
  NamedModbusClient,
  NamedModbusSerialClient,
  NamedModbusTcpClient,
} from '@/features/controller/configuration/models/raspiProperties/types'
import { NamedModbusTransport, SerialPort } from '@/features/controller/configuration/models/raspiProperties/types'
import {
  readNumberProperty,
  readPositiveNumberProperty,
  removePropertiesWithPrefix,
} from '@/features/controller/configuration/models/raspiProperties/utils'

const PROPS_MODBUS_CLIENTS_PREFIX = 'controller.modbus.clients.'
const PROPS_CONTROLLER_PREFIX = 'controller.'

/**
 * Property keys through which device families other than `modbusMeter` reference a named client.
 * `controller.metering.*` and `controller.steering.*` use `modbusConnection`, `controller.nwBattery`
 * uses `modbusClientName`. Matched by suffix, so any nesting depth is covered.
 */
const MODBUS_CLIENT_REFERENCE_KEY_SUFFIXES = ['.modbusConnection', '.modbusClientName']

export const DEFAULT_MODBUS_TCP_PORT = 502

/** The sympower-modbus defaults that apply when a key is absent. Shown in the UI, never written. */
export const NAMED_MODBUS_CLIENT_DEFAULTS = {
  connectTimeoutMs: 1000,
  responseTimeoutMs: 500,
  retries: 3,
} as const

/**
 * A client name is a single segment of the property key, so it must not contain a dot: with one,
 * `controller.modbus.clients.mel.plc.address` could not be told apart from a nested key.
 */
export const NAMED_MODBUS_CLIENT_NAME_PATTERN = /^[a-zA-Z0-9][a-zA-Z0-9_-]*$/

function readDeclaredClientNames(properties: Properties): string[] {
  return new Set(
    listProperties(properties)
      .filter(({ key }) => key.startsWith(PROPS_MODBUS_CLIENTS_PREFIX))
      .map(({ key }) => key.substring(PROPS_MODBUS_CLIENTS_PREFIX.length).split('.')[0])
      .filter((name) => name.length > 0),
  )
    .values()
    .toArray()
}

/**
 * Mirrors the controller's dispatch, which tests `baudRate` before `address`: a block declaring both
 * is RTU there, with the address ignored. Undefined for a block that declares neither, which the
 * controller rejects outright and which is therefore not ours to rewrite.
 */
function readClientTransport(properties: Properties, name: string): NamedModbusTransport | undefined {
  const prefix = PROPS_MODBUS_CLIENTS_PREFIX + name
  if (getProperty(properties, `${prefix}.baudRate`) !== undefined) {
    return NamedModbusTransport.serial
  }
  if (getProperty(properties, `${prefix}.address`) !== undefined) {
    return NamedModbusTransport.tcp
  }
  return undefined
}

function readTcpClient(properties: Properties, name: string): ConfigWithErrors<NamedModbusTcpClient> {
  const prefix = PROPS_MODBUS_CLIENTS_PREFIX + name
  const errors: string[] = []
  const address = getProperty(properties, `${prefix}.address`)
  if (!address) {
    errors.push(`Modbus connection '${name}' has no address configured`)
  }
  return {
    config: {
      address: address ?? '',
      connectTimeoutMs: extractErrors(
        readNumberProperty(properties, `${prefix}.connectTimeoutMs`, 'connect timeout'),
        errors,
      ),
      name,
      port: extractErrors(
        readPositiveNumberProperty(properties, `${prefix}.port`, 'port', DEFAULT_MODBUS_TCP_PORT),
        errors,
      ),
      responseTimeoutMs: extractErrors(
        readNumberProperty(properties, `${prefix}.responseTimeoutMs`, 'response timeout'),
        errors,
      ),
      retries: extractErrors(readNumberProperty(properties, `${prefix}.retries`, 'retries'), errors),
      transport: NamedModbusTransport.tcp,
    },
    errors,
  }
}

function readSerialClient(properties: Properties, name: string): ConfigWithErrors<NamedModbusSerialClient> {
  const prefix = PROPS_MODBUS_CLIENTS_PREFIX + name
  const errors: string[] = []
  const serialPort =
    extractErrors(readBareOrFullSerialPortName(getProperty(properties, `${prefix}.port`)), errors) ?? SerialPort.port1
  const mode = getProperty(properties, `${prefix}.mode`)
  if (mode === undefined) {
    // The controller has no default for it and refuses to start without one
    errors.push(`Modbus connection '${name}' has no serial mode configured`)
  }
  const { dataBits, parity, stopBits } = mode
    ? extractErrors(readSerialMode(mode), errors)
    : SERIAL_PORT_CONFIG_DEFAULTS
  return {
    config: {
      name,
      portConfig: {
        baudRate: extractErrors(
          readPositiveNumberProperty(
            properties,
            `${prefix}.baudRate`,
            'baud rate',
            SERIAL_PORT_CONFIG_DEFAULTS.baudRate,
          ),
          errors,
        ),
        dataBits,
        parity,
        stopBits,
      },
      responseTimeoutMs: extractErrors(
        readNumberProperty(properties, `${prefix}.responseTimeoutMs`, 'response timeout'),
        errors,
      ),
      retries: extractErrors(readNumberProperty(properties, `${prefix}.retries`, 'retries'), errors),
      serialPort,
      transport: NamedModbusTransport.serial,
    },
    errors,
  }
}

export function readNamedModbusClients(properties: Properties): ConfigWithErrors<NamedModbusClient>[] {
  return readDeclaredClientNames(properties).flatMap((name): ConfigWithErrors<NamedModbusClient>[] => {
    switch (readClientTransport(properties, name)) {
      case NamedModbusTransport.tcp:
        return [readTcpClient(properties, name)]
      case NamedModbusTransport.serial:
        return [readSerialClient(properties, name)]
      case undefined:
        return []
    }
  })
}

/**
 * Declared clients whose transport the controller cannot deduce either. Not modelled here, so they
 * are left out of the tree and preserved untouched rather than rewritten.
 */
export function readUnrecognisedModbusClientNames(properties: Properties): string[] {
  return readDeclaredClientNames(properties).filter((name) => readClientTransport(properties, name) === undefined)
}

function writeClientFields(properties: Properties, client: NamedModbusClient) {
  const prefix = PROPS_MODBUS_CLIENTS_PREFIX + client.name
  // Each branch clears the other transport's keys first, or the controller would dispatch on the
  // wrong one. `port` belongs to both and is rewritten either way, so it is never cleared.
  if (client.transport === NamedModbusTransport.tcp) {
    setProperty(properties, `${prefix}.baudRate`, undefined)
    setProperty(properties, `${prefix}.mode`, undefined)
    setProperty(properties, `${prefix}.address`, client.address)
    setProperty(properties, `${prefix}.port`, client.port.toString())
    setProperty(properties, `${prefix}.connectTimeoutMs`, client.connectTimeoutMs?.toString())
  } else {
    setProperty(properties, `${prefix}.address`, undefined)
    setProperty(properties, `${prefix}.connectTimeoutMs`, undefined)
    setProperty(properties, `${prefix}.port`, writeBareSerialPortName(client.serialPort))
    setProperty(properties, `${prefix}.baudRate`, client.portConfig.baudRate.toString())
    setProperty(properties, `${prefix}.mode`, writeSerialMode(client.portConfig))
  }
  // The timeouts and retries are genuinely optional: setProperty removes the key when the value is
  // undefined, so clearing a field in the UI gives the controller default back rather than pinning
  // the current one into the file.
  setProperty(properties, `${prefix}.responseTimeoutMs`, client.responseTimeoutMs?.toString())
  setProperty(properties, `${prefix}.retries`, client.retries?.toString())
}

/**
 * Write the named client blocks, removing the blocks of clients that are no longer in the model.
 * Only the modelled keys of a kept client are touched, so anything else under its prefix survives.
 */
export function writeNamedModbusClients(properties: Properties, clients: NamedModbusClient[]) {
  const keptNames = new Set(clients.map((client) => client.name))
  const unrecognisedNames = new Set(readUnrecognisedModbusClientNames(properties))
  for (const name of readDeclaredClientNames(properties)) {
    if (!keptNames.has(name) && !unrecognisedNames.has(name)) {
      removePropertiesWithPrefix(properties, `${PROPS_MODBUS_CLIENTS_PREFIX}${name}.`)
    }
  }
  for (const client of clients) {
    writeClientFields(properties, client)
  }
}

/** `controller.metering.pv1.modbusConnection` describes the metering channel `pv1`. */
function readReferenceSource(propertyKey: string, suffix: string) {
  const path = propertyKey.substring(PROPS_CONTROLLER_PREFIX.length, propertyKey.length - suffix.length)
  const separatorIndex = path.indexOf('.')
  if (separatorIndex === -1) {
    return { family: path }
  }
  return { deviceId: path.substring(separatorIndex + 1), family: path.substring(0, separatorIndex) }
}

/**
 * Plain-English nouns for the error messages below. The model layer has no translation, as its other
 * errors show, and these read differently from the UI's own family labels anyway.
 */
const EXTERNAL_FAMILY_NOUNS: Record<string, string> = {
  metering: 'metering channel',
  nwBattery: 'NW battery',
  steering: 'steering channel',
}

function describeExternalDevice({ deviceId, family }: ExternalModbusClientReference) {
  const noun = EXTERNAL_FAMILY_NOUNS[family] ?? `${family} configuration`
  return deviceId ? `The ${noun} '${deviceId}'` : `The ${noun}`
}

/**
 * A device of another family naming a connection nothing declares. It cannot be listed under any
 * connection, so say so rather than leaving it silently absent. Unlike a meter, its own
 * configuration is never rewritten here, so nothing is lost by leaving it as it is.
 */
export function describeDanglingExternalReferences(
  references: ExternalModbusClientReference[],
  declaredNames: string[],
  unrecognisedNames: string[],
): string[] {
  return references
    .filter((reference) => !declaredNames.includes(reference.clientName))
    .map((reference) => {
      const why = unrecognisedNames.includes(reference.clientName)
        ? 'is neither a Modbus/TCP nor a serial connection'
        : 'is not defined'
      return (
        `${describeExternalDevice(reference)} references Modbus connection '${reference.clientName}', which ${why}. ` +
        'It is not shown below, and its own configuration is left as it is.'
      )
    })
}

export function readExternalModbusClientReferences(properties: Properties): ExternalModbusClientReference[] {
  // toMap collapses a repeated key to its last value, which is the one the controller reads.
  // Iterating listProperties would instead yield one reference per line, showing a device twice and
  // holding a connection hostage to a reference that no longer applies.
  return [...toMap(properties)].flatMap(([key, value]) => {
    const suffix = MODBUS_CLIENT_REFERENCE_KEY_SUFFIXES.find((candidate) => key.endsWith(candidate))
    if (!key.startsWith(PROPS_CONTROLLER_PREFIX) || suffix === undefined) {
      return []
    }
    return [{ clientName: value, ...readReferenceSource(key, suffix), propertyKey: key }]
  })
}
